// 분노 유형 판정 (§9 개정: 결정적 우선순위. 로컬 곡선+행동만, 내용 안 봄).
//
// 각 유형은 겹치지 않는 대표 축을 하나씩 갖고, 우선순위 순서로 먼저 걸리는 게 이긴다
// (작은 곡선 차이로 유형이 흔들리지 않게). 같은 입력이면 같은 유형(결정적).
//
// 쓰는 특징(전부 내용 무관, 20버킷 곡선 + 행동):
//   강도     = 전체 행동 에너지 0~1 (behavior.rawB) — "게이지가 얼마나 움직였나"
//   삭제율   = 백스페이스 / 글자 수
//   작성 시간 = 총 시간(초)
//   피크 위치 = 主 피크가 타임라인 어디(0=처음, 1=끝)
//   피크 개수 = 의미 있는 봉우리 수 (主 피크의 60% 이상으로 올랐다, 40% 아래로 꺼졌다, 다시 오른 것만 별개)

import type { Archetype, BehaviorResult, AngerCurve, RawSignals } from "./types";
import { BUCKET_COUNT } from "./curve";

// 임계값(출발점, 넓게 벌려 튜닝). §9.
const INTENSITY_MIN = 0.25; // 미만이면 무풍형
const DELETE_RATE_MAX = 0.3; // 초과면 방화벽형
const SHORT_SEC = 8; // 미만이면 속전속결형
const LATE_PEAK = 0.65; // 초과면 활화산형
const PEAK_HIGH = 0.6; // 主 피크의 60%
const PEAK_LOW = 0.4; // 主 피크의 40%

/** 의미 있는 봉우리 수: ≥0.6으로 올랐다 <0.4로 꺼진 뒤 다시 오른 것만 별개로 셈(곡선은 피크 정규화돼 主=1). */
function countPeaks(points: number[]): number {
  let count = 0;
  let armed = true;
  for (const v of points) {
    if (v >= PEAK_HIGH && armed) {
      count++;
      armed = false;
    }
    if (v < PEAK_LOW) armed = true;
  }
  return count;
}

/** §9 우선순위 판정. 먼저 걸리는 게 이긴다. */
export function classify(
  s: RawSignals,
  behavior: BehaviorResult,
  curve: AngerCurve,
): Archetype {
  const intensity = behavior.rawB; // 강도 (전체 에너지 — 음성이면 음량 기반)
  // 삭제율 축: 텍스트=백스페이스율, 음성=멈춤 비율(말 삼킴·더듬거림 = 억눌림 → 방화벽형). §9.
  const isVoice = (s.mode === "voice" || s.mode === "mixed") && !!s.voice;
  const deleteRate = isVoice
    ? (s.voice?.pauseRatio ?? 0)
    : s.charCount > 0
      ? s.deleteCount / s.charCount
      : 0;
  const writeSec = s.durationMs / 1000; // 작성시간=발화시간(§9)
  const peakPos = curve.peakIndex / (BUCKET_COUNT - 1);
  const peakCount = curve.tooShort ? 0 : countPeaks(curve.points);

  if (intensity < INTENSITY_MIN) return "deadcalm"; // 강도 < 0.25
  if (deleteRate > DELETE_RATE_MAX) return "firewall"; // 삭제율 > 0.30
  if (writeSec < SHORT_SEC) return "cleancut"; // 작성 시간 < 8초
  if (peakCount >= 2) return "afterburn"; // 봉우리 ≥ 2 (식었다 재점화)
  if (peakPos > LATE_PEAK) return "slowburn"; // 피크 위치 > 0.65 (늦은 정점)
  return "cooler"; // 그 외: 앞쪽 단일 피크 후 하강
}
